import React from 'react';
import { useRouteMatch, Link, generatePath, Route, Switch, useHistory } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import routes from '../routes';
import { Roles } from '../models/roles';
import { currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite } from '../services/auth';
import { Colors } from '../assets/theme/Colors';
import DonationsListView from './DonationsListView';
import RescuesScheduleView from './RescuesScheduleView';
import PastRescuesListView from './PastRescuesListView';
import FutureRescuesListView from './FutureRescuesListView';
import useHasActiveFeatureFlag from '../hooks/useFeatureFlags';
import { FF_ROUTES } from '../components/FeatureFlagWrapper';
import RoutesContainer from '../pages/Routes/RoutesContainer';
import NewLabel from '../components/NewLabel';

const linkSx = {
  textDecoration: 'none',
  color: Colors.gray,
  paddingLeft: '10px',
  '&.active': {
    color: 'primary.main',
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: -3,
      top: 0,
      height: '100%',
      borderLeft: '4px solid',
      borderColor: 'primary.main',
    },
  },
};

const RescuesView = () => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const hasRoutesFF = useHasActiveFeatureFlag(FF_ROUTES);

  const rescuesList = [
    {
      name: 'Rescues',
      component: DonationsListView,
      path: generatePath(routes.rescuesList, { tab: 'rescues' }),
      tab: 'rescues',
    },
    {
      name: 'Routes',
      component: RoutesContainer,
      path: generatePath(routes.routes),
      tab: 'routes',
      isNew: true,
    },
    {
      name: 'Schedule',
      component: RescuesScheduleView,
      path: generatePath(routes.rescuesList, { tab: 'schedule' }),
      tab: 'schedule',
    },
    {
      name: 'Past Rescues',
      component: PastRescuesListView,
      path: generatePath(routes.rescuesList, { tab: 'past-rescues' }),
      tab: 'past-rescues',
    },
    {
      name: 'Upcoming Rescues',
      component: FutureRescuesListView,
      path: generatePath(routes.rescuesList, { tab: 'upcoming-rescues' }),
      tab: 'upcoming-rescues',
    },
  ];

  // remove Routes component from the react-router route switch, so it renders in seperate view
  const rescuesRoutes = rescuesList.filter((rescue) => rescue.name !== 'Routes');

  const userHasAcess = currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
    Roles.Admin,
    Roles.NationalSiteDirector,
    Roles.SiteDirector,
    Roles.SiteCoordinator,
  ]);

  const paths = rescuesList.map((rescue) => rescue.path);

  const getActiveTab = () => {
    const path = paths.indexOf(url);

    return Math.max(path, 0);
  };

  if (!userHasAcess) {
    history.push(routes.index);
  }

  return (
    <>
      <Typography variant="h5">Rescues</Typography>
      <Box mt={5} display="flex" gap="16px">
        <Box minWidth="max-content">
          {rescuesList.map((rescue, index) => (
            <Typography
              variant="subtitle2"
              sx={[rescue.name === 'Routes' && !hasRoutesFF && { display: 'none' }]} // hide behind FF
              key={rescue.name}
              gutterBottom
            >
              <Box
                component={Link}
                sx={linkSx}
                className={index !== getActiveTab() ? '' : 'active'}
                to={rescue.path}
                key={rescue.name}
              >
                {rescue.name}
              </Box>
              {rescue.isNew && <NewLabel featureFlag={FF_ROUTES} />}
            </Typography>
          ))}
        </Box>
        <Box sx={{ width: 'calc(100% - 170px)' }}>
          <Switch>
            {rescuesRoutes.map((rescue, index) => (
              <Route
                exact
                path={index === 0 ? [generatePath(routes.rescuesList), rescue.path] : rescue.path}
                component={rescue.component}
                key={rescue.name}
              />
            ))}
          </Switch>
        </Box>
      </Box>
    </>
  );
};

export default RescuesView;
